import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import { isPrimaryAdmin } from '../utils/roleRouting';
import {
  AlertTriangle,
  CheckCircle2,
  Database,
  Eye,
  RefreshCw,
  ShieldAlert,
  Trash2,
} from 'lucide-react';

const DEFAULT_BEFORE = '2026-01-28';

// Libellés des collections exclues : valeurs backend FR -> clés i18n (repli sur la valeur brute).
const EXCLUDED_LABELS = {
  'Réparations (devices)': 'adminCachePurge.excludedReparations',
  Stock: 'adminCachePurge.excludedStock',
  Clients: 'adminCachePurge.excludedClients',
  Utilisateurs: 'adminCachePurge.excludedUtilisateurs',
};

const AdminCachePurgeVentesPage = () => {
  const { api, user } = useAuth();
  const { t } = useLanguage();
  const [before, setBefore] = useState(DEFAULT_BEFORE);
  const [preview, setPreview] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewing, setPreviewing] = useState(false);
  const [confirm, setConfirm] = useState('');
  const [dryRun, setDryRun] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const expectedConfirm = `SUPPRIMER-${before}`;

  const loadMeta = useCallback(async () => {
    const res = await api.get('/admin/purge-ventes/meta');
    setMeta(res.data);
    if (res.data?.default_before) setBefore(res.data.default_before);
  }, [api]);

  const loadPreview = useCallback(async () => {
    setPreviewing(true);
    setError(null);
    try {
      const res = await api.get('/admin/purge-ventes/preview', { params: { before } });
      setPreview(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || t('adminCachePurge.errorPreview'));
      setPreview(null);
    } finally {
      setPreviewing(false);
    }
  }, [api, before, t]);

  useEffect(() => {
    if (!isPrimaryAdmin(user)) return;
    (async () => {
      setLoading(true);
      try {
        await loadMeta();
        await loadPreview();
      } catch (e) {
        setError(e?.response?.data?.detail || t('adminCachePurge.errorLoad'));
      } finally {
        setLoading(false);
      }
    })();
  }, [user, loadMeta, loadPreview, t]);

  const totalDocs = preview?.total_documents ?? 0;
  const canExecute = confirm.trim() === expectedConfirm && totalDocs > 0;

  const excluded = useMemo(
    () => meta?.excluded || ['Réparations (devices)', 'Stock', 'Clients', 'Utilisateurs'],
    [meta]
  );

  const handleExecute = async () => {
    if (!canExecute) return;
    if (
      !window.confirm(
        dryRun
          ? t('adminCachePurge.confirmDryRun', { before, count: totalDocs })
          : t('adminCachePurge.confirmDelete', { count: totalDocs, before })
      )
    ) {
      return;
    }
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const res = await api.post('/admin/purge-ventes/execute', {
        before,
        confirm: expectedConfirm,
        dry_run: dryRun,
      });
      setResult(res.data);
      if (!dryRun) {
        setConfirm('');
        await loadPreview();
      }
    } catch (e) {
      setError(e?.response?.data?.detail || t('adminCachePurge.errorPurge'));
    } finally {
      setRunning(false);
    }
  };

  if (!isPrimaryAdmin(user)) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[50vh] text-gray-400">
          {t('adminCachePurge.accessDenied')}
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="loading-spinner" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto fade-in space-y-6" data-testid="admin-cache-purge-ventes">
        <div className="flex items-start gap-3">
          <ShieldAlert className="w-9 h-9 text-red-400 shrink-0 mt-1" />
          <div>
            <h1 className="text-2xl font-bold text-white">{t('adminCachePurge.title')}</h1>
            <p className="text-sm text-gray-400 mt-1">
              {t('adminCachePurge.descIntro')}
              <strong className="text-amber-300"> {t('adminCachePurge.descBefore')} </strong>
              {t('adminCachePurge.descOutro')}
            </p>
          </div>
        </div>

        <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-200 flex gap-3">
          <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
          <div>
            {t('adminCachePurge.warnBackupBefore')}
            {' '}
            <a href="/admin/database" className="underline text-red-100">{t('adminCachePurge.warnBackupLink')}</a>
            {' '}
            {t('adminCachePurge.warnBackupAfter')}
          </div>
        </div>

        {error && (
          <div className="rounded-xl border border-red-500/40 bg-red-500/15 px-4 py-3 text-red-200 text-sm">
            {typeof error === 'string' ? error : JSON.stringify(error)}
          </div>
        )}

        {result && (
          <div className={`rounded-xl border px-4 py-3 text-sm flex gap-2 ${result.dry_run ? 'border-amber-500/40 bg-amber-500/10 text-amber-100' : 'border-emerald-500/40 bg-emerald-500/10 text-emerald-100'}`}>
            <CheckCircle2 className="w-5 h-5 shrink-0" />
            <div>
              {t(
                result.dry_run ? 'adminCachePurge.resultDryRunLine' : 'adminCachePurge.resultPurgeLine',
                { count: result.total_deleted, before: result.before }
              )}
            </div>
          </div>
        )}

        <div className="glass p-6 rounded-2xl space-y-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Database className="w-5 h-5 text-cyan-300" />
            {t('adminCachePurge.sectionParams')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-300 mb-1">{t('adminCachePurge.labelBeforeDate')}</label>
              <input
                type="date"
                value={before}
                onChange={(e) => setBefore(e.target.value)}
                className="input-dark w-full"
                data-testid="purge-before-date"
              />
              <p className="text-xs text-gray-500 mt-1">{t('adminCachePurge.hintStrictlyBefore')}</p>
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-1">{t('adminCachePurge.labelConfirm')}</label>
              <input
                type="text"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder={expectedConfirm}
                className="input-dark w-full font-mono"
                data-testid="purge-confirm-input"
              />
              <p className="text-xs text-gray-500 mt-1">{t('adminCachePurge.hintTypeToken', { token: expectedConfirm })}</p>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
            <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} className="rounded" />
            {t('adminCachePurge.dryRunLabel')}
          </label>

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={loadPreview}
              disabled={previewing}
              className="px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white font-medium flex items-center gap-2 disabled:opacity-50"
              data-testid="purge-preview-btn"
            >
              {previewing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
              {t('adminCachePurge.previewBtn')}
            </button>
            <button
              type="button"
              onClick={handleExecute}
              disabled={!canExecute || running}
              className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white font-medium flex items-center gap-2 disabled:opacity-50"
              data-testid="purge-execute-btn"
            >
              {running ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              {dryRun ? t('adminCachePurge.simulateBtn') : t('adminCachePurge.executeBtn')}
            </button>
          </div>
        </div>

        <div className="glass p-6 rounded-2xl">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">{t('adminCachePurge.previewHeading', { count: totalDocs })}</h2>
            <span className="text-xs text-gray-400">{t('adminCachePurge.beforeLabel', { before })}</span>
          </div>
          {!preview?.collections?.length ? (
            <p className="text-gray-500 text-sm">{t('adminCachePurge.previewEmpty')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-400 border-b border-white/10">
                    <th className="py-2 pr-4">{t('adminCachePurge.colCollection')}</th>
                    <th className="py-2 pr-4 text-right">{t('adminCachePurge.colCount')}</th>
                    <th className="py-2">{t('adminCachePurge.colExamples')}</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.collections.map((row) => (
                    <tr key={row.key} className="border-b border-white/5">
                      <td className="py-2.5 pr-4">
                        <div className="font-medium text-white">{row.label}</div>
                        {row.description && <div className="text-xs text-gray-500">{row.description}</div>}
                      </td>
                      <td className={`py-2.5 pr-4 text-right font-bold ${row.count ? 'text-amber-300' : 'text-gray-500'}`}>
                        {row.count}
                      </td>
                      <td className="py-2.5 text-xs text-gray-400 font-mono truncate max-w-md">
                        {(row.samples || []).join(', ') || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="glass p-6 rounded-2xl">
          <h2 className="text-sm font-semibold text-gray-300 mb-2">{t('adminCachePurge.sectionExcluded')}</h2>
          <ul className="text-sm text-gray-500 list-disc list-inside space-y-1">
            {excluded.map((line) => (
              <li key={line}>{EXCLUDED_LABELS[line] ? t(EXCLUDED_LABELS[line]) : line}</li>
            ))}
          </ul>
        </div>
      </div>
    </Layout>
  );
};

export default AdminCachePurgeVentesPage;
